import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, LogIn, User, LogOut, ChevronDown, Shield, GraduationCap, Briefcase, KeyRound } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useHubMember } from "@/lib/HubMemberContext";
import { BrandLogo } from "./BrandLogo";

const publicLinks = [
  { to: "/", label: "Home" },
];

const academyLinks = [
  { slug: "facilities",      label: "Facilities" },
  { slug: "advantage",       label: "Advantage" },
  { slug: "life-at-academy", label: "Life at Academy" },
  { slug: "achievements",    label: "Achievements" },
  { slug: "pathway",         label: "Pathway" },
  { slug: "sessions",        label: "Sessions" },
  { slug: "fees-structure",  label: "Fees Structure" },
];

const tailLinks = [
  { to: "/tournaments", label: "Tournaments" },
  { to: "/team",        label: "About Us" },
  { to: "/gallery",     label: "Gallery" },
  { to: "/blogs",       label: "Journal" },
  { to: "/contact",     label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [acadOpen, setAcadOpen] = useState(false);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const acadRef = useRef(null);
  const adminMenuRef = useRef(null);
  const { member: hubMember } = useHubMember();

  const doLogout = async () => { await logout(); nav("/"); };

  // click-outside close for the dropdowns
  useEffect(() => {
    const onClick = (e) => {
      if (acadRef.current && !acadRef.current.contains(e.target)) setAcadOpen(false);
      if (adminMenuRef.current && !adminMenuRef.current.contains(e.target)) setAdminMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const isAcademy = loc.pathname === "/academy";

  return (
    <header className="glass sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between h-16">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
          <BrandLogo as="div" size="md" showWordmark={false} />
          <span className="font-display text-base sm:text-xl font-black tracking-tight uppercase whitespace-nowrap">
            HTP Sports Hub
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-6 font-medium text-sm">
          {publicLinks.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g,'-')}`}
              className={({ isActive }) => `whitespace-nowrap hover:text-white transition-colors ${isActive ? "brand-blue" : "text-white/70"}`}>
              {l.label}
            </NavLink>
          ))}

          {/* Academy Dropdown */}
          <div className="relative" ref={acadRef}>
            <button
              onClick={() => setAcadOpen(!acadOpen)}
              data-testid="nav-academy"
              className={`flex items-center gap-1 whitespace-nowrap hover:text-white transition-colors ${isAcademy ? "brand-blue" : "text-white/70"}`}
            >
              Academy <ChevronDown size={14} className={`transition-transform ${acadOpen ? "rotate-180" : ""}`} />
            </button>
            {acadOpen && (
              <div
                data-testid="nav-academy-dropdown"
                className="absolute top-full right-0 mt-3 w-64 bg-[#0B1120] border border-white/12 shadow-2xl"
              >
                <Link to="/academy" onClick={() => setAcadOpen(false)}
                  data-testid="nav-academy-overview"
                  className="block px-5 py-3 text-xs font-mono uppercase tracking-widest border-b border-white/8 brand-orange hover:bg-white/6">
                  Academy Overview
                </Link>
                {academyLinks.map((l) => (
                  <Link
                    key={l.slug}
                    to={`/academy/${l.slug}`}
                    onClick={() => setAcadOpen(false)}
                    data-testid={`nav-academy-${l.label.toLowerCase().replace(/\s+/g,'-')}`}
                    className="block px-5 py-2.5 text-sm text-white/80 hover:bg-white/6 hover:text-white border-l-2 border-transparent hover:border-brand-orange"
                  >
                    {l.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {tailLinks.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g,'-')}`}
              className={({ isActive }) => `whitespace-nowrap hover:text-white transition-colors ${isActive ? "brand-blue" : "text-white/70"}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <Link
                to={user.role === "admin" || user.role === "staff" ? "/admin" : user.role === "coach" ? "/coach" : user.role === "member" ? "/member" : "/parent"}
                data-testid="nav-dashboard"
                className="text-white/80 hover:text-white text-sm flex items-center gap-2 px-3 py-1.5 whitespace-nowrap">
                <User size={16} /> {user.name?.split(" ")[0]}
              </Link>
              <button onClick={doLogout} data-testid="nav-logout"
                className="text-xs font-mono uppercase tracking-widest text-white/60 hover:text-white flex items-center gap-1.5 whitespace-nowrap">
                <LogOut size={14} /> Logout
              </button>
            </>
          ) : (
            <>
              {hubMember ? (
                <Link to="/me" data-testid="nav-member-chip"
                  className="inline-flex items-center gap-2 border border-brand-blue/40 bg-brand-blue/10 text-white pl-1.5 pr-3 py-1 rounded-full hover:bg-brand-blue hover:text-white transition">
                  {hubMember.picture ? (
                    <img src={hubMember.picture} alt="" className="w-6 h-6 rounded-full" />
                  ) : (
                    <span className="w-6 h-6 rounded-full bg-brand-blue/30 grid place-items-center text-[10px] font-black uppercase">
                      {(hubMember.name || "M").charAt(0)}
                    </span>
                  )}
                  <span className="text-[11px] font-mono uppercase tracking-widest whitespace-nowrap">
                    {(hubMember.name || "Member").split(" ")[0]}
                  </span>
                </Link>
              ) : (
                <Link
                  to="/member/login"
                  data-testid="nav-member-login"
                  className="inline-flex items-center gap-2 bg-brand-orange text-black px-4 py-2 font-bold text-xs uppercase tracking-widest hover:bg-white hover:glow-orange transition-all whitespace-nowrap"
                >
                  <LogIn size={14} /> Sign in / Sign up
                </Link>
              )}

              {/* Administration Login — combined Coach / Staff / Admin */}
              <div className="relative" ref={adminMenuRef}>
                <button
                  onClick={() => setAdminMenuOpen(!adminMenuOpen)}
                  data-testid="nav-admin-menu"
                  className="inline-flex items-center gap-1.5 border border-white/25 text-white/80 hover:text-white hover:border-white/60 px-3 py-2 text-[11px] font-mono uppercase tracking-widest whitespace-nowrap transition"
                >
                  <Shield size={12} /> Administration
                  <ChevronDown size={12} className={`transition-transform ${adminMenuOpen ? "rotate-180" : ""}`} />
                </button>
                {adminMenuOpen && (
                  <div
                    data-testid="nav-admin-menu-dropdown"
                    className="absolute top-full right-0 mt-2 w-64 bg-[#0B1120] border border-white/15 shadow-2xl"
                  >
                    <div className="px-4 py-3 border-b border-white/10">
                      <div className="font-mono text-[10px] uppercase tracking-[0.3em] brand-orange">// Certified access</div>
                      <div className="text-[11px] text-white/60 mt-1">Use your issued credentials to sign in.</div>
                    </div>
                    <Link
                      to="/coach/login"
                      onClick={() => setAdminMenuOpen(false)}
                      data-testid="nav-coach-login"
                      className="flex items-start gap-3 px-4 py-3 hover:bg-white/6 border-l-2 border-transparent hover:border-brand-blue transition"
                    >
                      <GraduationCap size={16} className="brand-blue mt-0.5 shrink-0" />
                      <div>
                        <div className="text-sm font-bold uppercase tracking-wider">Coach Login</div>
                        <div className="text-[10px] text-white/50 mt-0.5">Attendance · batches · reports</div>
                      </div>
                    </Link>
                    <Link
                      to="/management/login"
                      onClick={() => setAdminMenuOpen(false)}
                      data-testid="nav-staff-login"
                      className="flex items-start gap-3 px-4 py-3 hover:bg-white/6 border-l-2 border-transparent hover:border-brand-blue transition"
                    >
                      <Briefcase size={16} className="brand-blue mt-0.5 shrink-0" />
                      <div>
                        <div className="text-sm font-bold uppercase tracking-wider">Staff Login</div>
                        <div className="text-[10px] text-white/50 mt-0.5">Desk · reservations · check-ins</div>
                      </div>
                    </Link>
                    <Link
                      to="/admin/login"
                      onClick={() => setAdminMenuOpen(false)}
                      data-testid="nav-admin-login"
                      className="flex items-start gap-3 px-4 py-3 hover:bg-white/6 border-l-2 border-transparent hover:border-brand-orange transition"
                    >
                      <KeyRound size={16} className="brand-orange mt-0.5 shrink-0" />
                      <div>
                        <div className="text-sm font-bold uppercase tracking-wider">Admin Login</div>
                        <div className="text-[10px] text-white/50 mt-0.5">Full ERP · founders only</div>
                      </div>
                    </Link>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <button onClick={() => setOpen(!open)} className="md:hidden text-white" data-testid="nav-mobile-toggle">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      {open && (
        <div className="md:hidden bg-black border-t border-white/10 px-5 py-4 space-y-3">
          {publicLinks.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block text-white/80 py-1">{l.label}</Link>
          ))}
          <div className="py-1">
            <Link to="/academy" onClick={() => setOpen(false)} className="block brand-orange font-bold py-1">The Academy →</Link>
            <div className="pl-3 space-y-1 mt-1">
              {academyLinks.map((l) => (
                <Link key={l.slug} to={`/academy/${l.slug}`} onClick={() => setOpen(false)} className="block text-white/60 text-sm py-0.5">
                  · {l.label}
                </Link>
              ))}
            </div>
          </div>
          {tailLinks.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block text-white/80 py-1">{l.label}</Link>
          ))}
          <div className="pt-3 border-t border-white/10 space-y-2">
            {user ? (
              <>
                <Link to={user.role === "admin" || user.role === "staff" ? "/admin" : user.role === "coach" ? "/coach" : user.role === "member" ? "/member" : "/parent"} onClick={() => setOpen(false)} className="block text-white/80">
                  {user.name} · Dashboard
                </Link>
                <button onClick={() => { setOpen(false); doLogout(); }} className="text-white/60 text-sm">Logout</button>
              </>
            ) : (
              <>
                <Link
                  to="/member/login"
                  onClick={() => setOpen(false)}
                  data-testid="nav-mobile-member-login"
                  className="block bg-brand-orange text-black px-4 py-3 text-center font-bold text-xs uppercase tracking-widest"
                >
                  Sign in / Sign up
                </Link>
                <div className="pt-3 mt-2 border-t border-white/10">
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] brand-orange mb-2">// Administration Login</div>
                  <Link to="/coach/login" onClick={() => setOpen(false)} className="block text-white/60 py-1 text-sm">· Coach Login</Link>
                  <Link to="/management/login" onClick={() => setOpen(false)} className="block text-white/60 py-1 text-sm">· Staff Login</Link>
                  <Link to="/admin/login" onClick={() => setOpen(false)} className="block text-white/60 py-1 text-sm">· Admin Login</Link>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
